import VariantPicker from '@/scripts/components/VariantPicker';
import {addToCart, serializeCartForm} from '@/scripts/components/cartAdd';
import waitlistForm from '@/scripts/components/waitlistForm';

const SELECTORS = {
    form: '.collection-product-card__form',
    json: '[data-collection-product-card-json]',
    optionInput: '.collection-product-card__option-input',
    optionSelect: '.collection-product-card__select',
    status: '[data-cpc-status]',
    submit: '.collection-product-card__size--submit',
    waitlistTrigger: '[data-cpc-waitlist]',
    waitlistPanel: '[data-cpc-waitlist-panel]',
    waitlistInner: '.collection-product-card__waitlist-inner',
    waitlistClose: '[data-cpc-waitlist-close]',
    waitlistMount: '[data-cpc-waitlist-mount]',
    waitlistName: '[data-cpc-waitlist-name]',
    waitlistEmail: 'input[type="email"]',
    dropdown: '[data-cpc-dropdown]',
    dropdownTrigger: '[data-cpc-dropdown-trigger]',
    dropdownValue: '[data-cpc-dropdown-value]',
    dropdownList: '[role="listbox"]',
    dropdownOption: '[data-cpc-dropdown-option]',
    dropdownOptionLabel: '.collection-product-card__dropdown-option-label',
};

const STATUS_TIMEOUT = 4000;

const WAITLIST_OPEN_EVENT = 'cpc:waitlist-open';

const ROVING_KEYS = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];

export default class CollectionProductCard {
    constructor(root) {
        this.root = root;
        this.form = root.querySelector(SELECTORS.form);
        this.status = root.querySelector(SELECTORS.status);
        this.pending = false;
        this.statusTimer = null;
        this.suppressClick = false;

        if (!this.form) {
            return;
        }

        this.bindWaitlist();

        this.product = this.readProduct();
        if (!this.product) {
            return;
        }

        this.onClick = this.onClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onKeyup = this.onKeyup.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onSubmit = this.onSubmit.bind(this);

        this.bind();
    }

    readProduct() {
        const script = this.form.querySelector(SELECTORS.json);
        if (!script) {
            return null;
        }

        try {
            return JSON.parse(script.textContent);
        } catch (error) {
            console.error('[CollectionProductCard] bad product JSON', error);
            return null;
        }
    }

    bind() {
        this.form.addEventListener('click', this.onClick);
        this.form.addEventListener('keydown', this.onKeydown);
        this.form.addEventListener('keyup', this.onKeyup);
        this.form.addEventListener('change', this.onChange);
        this.form.addEventListener('submit', this.onSubmit);

        if (this.root.hasAttribute('data-cpc-default-variant')) {
            return;
        }

        this.ensureCheckedOptions();

        this.picker = new VariantPicker(this.form, this.product, {
            syncUrl: false,
            readInboundVariant: false,
            listenForExternalSelect: false,
            // Not `document`: a PLP holds ~20 pickers, and every one of them
            // dispatching `variant:change` globally would be noise for anything
            // that listens for the PDP's single picker.
            eventTarget: this.form,
        });

        // With a `<select>` on the card, VariantPicker binds to it directly and
        // resolves the variant before the change bubbles to `onChange`.
        if (!this.form.querySelector(SELECTORS.optionSelect)) {
            this.picker.init();
        }

        this.bindDropdown();
    }

    /**
     * The snippet only emits a radio for values it can sell — a sold-out value
     * becomes a waitlist `<a>` instead — and it marks the one matching
     * `option.selected_value`. On a product with two or more options, if the
     * selected value of one of them is sold out that group ends up with nothing
     * checked, `VariantPicker` cannot resolve a variant, and every cell on the
     * card fails. Fall back to that group's first sellable value.
     *
     * Setting `.checked` does not fire `change`, so this cannot add to cart.
     */
    ensureCheckedOptions() {
        const inputs = Array.from(this.form.querySelectorAll(SELECTORS.optionInput));
        if (!inputs.length) {
            return;
        }

        const groups = new Map();
        inputs.forEach((input) => {
            if (!groups.has(input.name)) {
                groups.set(input.name, []);
            }
            groups.get(input.name).push(input);
        });

        groups.forEach((group) => {
            if (!group.some((input) => input.checked)) {
                group[0].checked = true;
            }
        });
    }

    /**
     * The `select` layout renders the designed listbox over a visually hidden
     * native `<select>`. The select stays the single source of truth — this only
     * mirrors it — so VariantPicker and the add-to-cart path are untouched.
     * Merchandising opts a product in with `product_card.option_layout: select`.
     */
    bindDropdown() {
        this.dropdown = this.form.querySelector(SELECTORS.dropdown);
        if (!this.dropdown) {
            return;
        }

        this.dropdownSelect = this.dropdown.querySelector(SELECTORS.optionSelect);
        this.dropdownTrigger = this.dropdown.querySelector(SELECTORS.dropdownTrigger);
        this.dropdownValue = this.dropdown.querySelector(SELECTORS.dropdownValue);
        this.dropdownList = this.dropdown.querySelector(SELECTORS.dropdownList);
        this.dropdownOptions = Array.from(this.dropdown.querySelectorAll(SELECTORS.dropdownOption));

        if (!this.dropdownSelect || !this.dropdownTrigger || !this.dropdownValue || !this.dropdownList) {
            this.dropdown = null;
            return;
        }

        this.dropdownPlaceholder = this.dropdownValue.textContent;
        this.dropdownActive = -1;

        this.onDropdownClick = this.onDropdownClick.bind(this);
        this.onDropdownKeydown = this.onDropdownKeydown.bind(this);
        this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);

        this.dropdown.addEventListener('click', this.onDropdownClick);
        this.dropdown.addEventListener('keydown', this.onDropdownKeydown);
        document.addEventListener('pointerdown', this.onDocumentPointerDown);
    }

    get dropdownOpen() {
        return this.dropdownTrigger.getAttribute('aria-expanded') === 'true';
    }

    onDropdownClick(event) {
        if (event.target.closest(SELECTORS.dropdownTrigger)) {
            this.toggleDropdown(!this.dropdownOpen);
            return;
        }

        const option = event.target.closest(SELECTORS.dropdownOption);
        if (option) {
            this.chooseDropdownOption(this.dropdownOptions.indexOf(option));
        }
    }

    onDropdownKeydown(event) {
        const {key} = event;

        if (key === 'Escape') {
            if (this.dropdownOpen) {
                event.preventDefault();
                this.toggleDropdown(false);
            }
            return;
        }

        if (key === 'Tab') {
            this.toggleDropdown(false);
            return;
        }

        if (!this.dropdownOpen) {
            if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter' || key === ' ') {
                event.preventDefault();
                this.toggleDropdown(true, {activate: true});
            }
            return;
        }

        if (key === 'Enter' || key === ' ') {
            event.preventDefault();
            // Opened by pointer, so nothing is highlighted yet — close rather
            // than commit a row the shopper never moved to.
            if (this.dropdownActive === -1) {
                this.toggleDropdown(false);
                return;
            }

            this.chooseDropdownOption(this.dropdownActive);
            return;
        }

        const last = this.dropdownOptions.length - 1;
        let next = this.dropdownActive;

        if (key === 'ArrowDown') {
            next = this.dropdownActive < last ? this.dropdownActive + 1 : 0;
        } else if (key === 'ArrowUp') {
            next = this.dropdownActive > 0 ? this.dropdownActive - 1 : last;
        } else if (key === 'Home') {
            next = 0;
        } else if (key === 'End') {
            next = last;
        } else {
            return;
        }

        event.preventDefault();
        this.setDropdownActive(next);
    }

    onDocumentPointerDown(event) {
        if (this.dropdownOpen && !this.dropdown.contains(event.target)) {
            this.toggleDropdown(false, {restoreFocus: false});
        }
    }

    /**
     * `activate` is the keyboard's roving-index starting point. Pointer opens
     * pass it as false so no row is highlighted until the shopper hovers one —
     * otherwise the first denomination looks pre-selected.
     */
    toggleDropdown(open, {activate = false, restoreFocus = true} = {}) {
        // Read this before hiding the list: `hidden` on the focused element
        // moves focus to <body>, so checking afterwards always says "outside".
        const hadFocusInside = this.dropdown.contains(document.activeElement);

        this.dropdownTrigger.setAttribute('aria-expanded', open ? 'true' : 'false');
        this.dropdownList.hidden = !open;
        this.dropdown.classList.toggle('is-open', open);
        // The open list covers the card's image and copy, and every card is its own
        // stacking context — without this the next card in the grid paints over it.
        this.root.classList.toggle('is-dropdown-open', open);

        if (!open) {
            this.setDropdownActive(-1);

            if (restoreFocus) {
                this.dropdownTrigger.focus();
            } else if (hadFocusInside) {
                // An outside pointer closed the list. Pulling the viewport back
                // to this card would be a scroll jump on a grid of cards, but
                // focus cannot be left on the element we are about to hide —
                // so move it without scrolling. The browser's own mousedown
                // focus still wins for whatever the shopper actually clicked.
                this.dropdownTrigger.focus({preventScroll: true});
            }

            return;
        }

        const selected = this.dropdownOptions.findIndex(
            (option) => option.dataset.value === this.dropdownSelect.value,
        );

        this.dropdownList.focus();

        if (selected > -1) {
            this.setDropdownActive(selected);
        } else {
            this.setDropdownActive(activate ? 0 : -1);
        }
    }

    setDropdownActive(index) {
        this.dropdownActive = index;

        this.dropdownOptions.forEach((option, i) => {
            option.classList.toggle('is-active', i === index);
        });

        const active = this.dropdownOptions[index];
        if (active) {
            this.dropdownList.setAttribute('aria-activedescendant', active.id);
            if (typeof active.scrollIntoView === 'function') {
                active.scrollIntoView({block: 'nearest'});
            }
        } else {
            this.dropdownList.removeAttribute('aria-activedescendant');
        }
    }

    chooseDropdownOption(index) {
        const option = this.dropdownOptions[index];
        if (!option) {
            return;
        }

        if (option.getAttribute('aria-disabled') === 'true') {
            // A sold-out row is a waitlist trigger, exactly as the sold-out
            // cell of the segmented box is. Close the list first so the panel
            // is not covered, then hand the row to the same opener. The click
            // path would also reach `onWaitlistClick` on the root, but the
            // keyboard path (Enter on a highlighted row) would not.
            if (option.hasAttribute('data-cpc-waitlist')) {
                this.toggleDropdown(false);
                this.openWaitlist(option);
            }

            return;
        }

        this.dropdownSelect.value = option.dataset.value;
        this.toggleDropdown(false);
        this.syncDropdown();

        // VariantPicker listens on the select itself, so it resolves the variant
        // before this bubbles up to the card's own `change` handler, which adds.
        this.dropdownSelect.dispatchEvent(new Event('change', {bubbles: true}));
    }

    /** Mirror the native select's state onto the trigger and the rows. */
    syncDropdown() {
        if (!this.dropdown) {
            return;
        }

        const {value} = this.dropdownSelect;
        const selected = this.dropdownOptions.find((option) => option.dataset.value === value);

        const selectedLabel = selected && selected.querySelector(SELECTORS.dropdownOptionLabel);

        this.dropdownValue.textContent = selectedLabel
            ? selectedLabel.textContent.trim()
            : this.dropdownPlaceholder;

        this.dropdownTrigger.classList.toggle('is-placeholder', !selected);

        this.dropdownOptions.forEach((option) => {
            option.setAttribute('aria-selected', option === selected ? 'true' : 'false');
        });
    }

    onClick(event) {
        const input = event.target.closest(SELECTORS.optionInput);
        if (!input) {
            return;
        }

        if (this.suppressClick) {
            this.suppressClick = false;
            return;
        }

        this.submit(this.cellFor(input));
    }

    onKeydown(event) {
        const input = event.target.closest(SELECTORS.optionInput);
        if (!input) {
            return;
        }

        if (ROVING_KEYS.includes(event.key)) {
            this.suppressClick = true;
            return;
        }

        if (event.key === 'Enter') {
            event.preventDefault();
            this.submit(this.cellFor(input));
        }
    }

    onKeyup() {
        this.suppressClick = false;
    }

    bindWaitlist() {
        this.panel = this.root.querySelector(SELECTORS.waitlistPanel);
        if (!this.panel) {
            return;
        }

        this.waitlistMount = this.panel.querySelector(SELECTORS.waitlistMount);
        if (!this.waitlistMount) {
            this.panel = null;
            return;
        }

        this.waitlistName = this.panel.querySelector(SELECTORS.waitlistName);
        this.waitlistTrigger = null;

        this.onWaitlistClick = this.onWaitlistClick.bind(this);
        this.onWaitlistKeydown = this.onWaitlistKeydown.bind(this);
        this.onOtherWaitlistOpen = this.onOtherWaitlistOpen.bind(this);

        this.root.addEventListener('click', this.onWaitlistClick);
        this.root.addEventListener('keydown', this.onWaitlistKeydown);
        document.addEventListener(WAITLIST_OPEN_EVENT, this.onOtherWaitlistOpen);
    }

    onWaitlistClick(event) {
        if (event.target.closest(SELECTORS.waitlistClose)) {
            event.preventDefault();
            this.closeWaitlist();
            return;
        }

        if (
            event.target.closest(SELECTORS.waitlistPanel) &&
            !event.target.closest(SELECTORS.waitlistInner)
        ) {
            this.closeWaitlist();
            return;
        }

        const trigger = event.target.closest(SELECTORS.waitlistTrigger);
        if (!trigger) {
            return;
        }

        event.preventDefault();
        this.openWaitlist(trigger);
    }

    onWaitlistKeydown(event) {
        if (event.key === 'Escape' && !this.panel.hidden) {
            this.closeWaitlist();
        }
    }

    onOtherWaitlistOpen(event) {
        if (event.detail && event.detail.card !== this.root) {
            this.hideWaitlist();
        }
    }

    openWaitlist(trigger) {
        // The dropdown's own handler opens the panel for a sold-out row, and
        // the same click then bubbles to `onWaitlistClick` on the root. Without
        // this the snippet would mount a second form into the same node.
        if (this.waitlistTrigger === trigger && !this.panel.hidden) {
            return;
        }

        const variantId = trigger.dataset.cpcWaitlistVariant;
        const variant = this.variantById(variantId);

        // With more than one option the single option value the cell carries
        // does not name the variant — say the whole thing, as the PDP does.
        const value = (variant && variant.options && variant.options.length > 1)
            ? variant.options.join(' / ')
            : (trigger.dataset.cpcWaitlistValue || '');

        this.waitlistTrigger = trigger;

        if (this.waitlistName) {
            this.waitlistName.textContent = value;
        }

        this.panel.hidden = false;
        this.root.classList.add('is-waitlist-open');

        // The snippet decides whether to render at all from `variant` +
        // `waitlist`; passing neither left that check dead on this surface, so
        // an in-stock variant would still get a waitlist form.
        const options = {
            el: this.waitlistMount,
            variantId,
            variantName: value,
            variant,
            waitlist: variant ? !!variant.waitlist : true,
        };

        const listId = this.panel.dataset.cpcWaitlistList;
        if (listId) {
            options.listId = listId;
        }

        waitlistForm(options);

        // The snippet prepends its own "notify me when X is back" copy, which
        // the panel already renders as its title. Drop the duplicate here
        // rather than hiding every `p` in CSS — the success confirmation the
        // snippet swaps in on submit is a `p` too.
        const intro = this.waitlistMount.querySelector('.waitlist > p');
        if (intro) {
            intro.remove();
        }

        this.watchWaitlistSuccess();

        if (typeof window.GAevent === 'function') {
            window.GAevent('Collection', 'Sold Out - Waitlist', 'Open');
        }

        document.dispatchEvent(
            new CustomEvent(WAITLIST_OPEN_EVENT, {detail: {card: this.root}}),
        );

        const email = this.waitlistMount.querySelector(SELECTORS.waitlistEmail);
        if (email) {
            email.focus();
        }
    }

    /**
     * A successful signup replaces the whole `.waitlist` container with a
     * thank-you paragraph, so the form disappearing is the only signal we get.
     * Once it does, the panel title is redundant.
     */
    watchWaitlistSuccess() {
        this.stopWatchingWaitlist();

        this.waitlistObserver = new MutationObserver(() => {
            const done = !this.waitlistMount.querySelector('.waitlist-form');
            this.panel.classList.toggle('is-waitlist-success', done);
        });

        this.waitlistObserver.observe(this.waitlistMount, {childList: true, subtree: true});
    }

    stopWatchingWaitlist() {
        if (this.waitlistObserver) {
            this.waitlistObserver.disconnect();
            this.waitlistObserver = null;
        }

        this.panel.classList.remove('is-waitlist-success');
    }

    closeWaitlist() {
        const trigger = this.waitlistTrigger;

        this.hideWaitlist();

        if (trigger) {
            trigger.focus();
        }
    }

    hideWaitlist() {
        this.stopWatchingWaitlist();

        this.panel.hidden = true;
        this.root.classList.remove('is-waitlist-open');
        this.waitlistTrigger = null;

        if (this.waitlistMount) {
            this.waitlistMount.innerHTML = '';
        }
    }

    onChange(event) {
        const select = event.target.closest(SELECTORS.optionSelect);
        if (!select) {
            return;
        }

        if (!this.picker || !this.picker.currentVariant) {
            return;
        }

        this.submit(select);
    }

    onSubmit(event) {
        event.preventDefault();

        event.stopImmediatePropagation();

        this.submit(this.form.querySelector(SELECTORS.submit));
    }

    cellFor(input) {
        return input.nextElementSibling;
    }

    /** The card's own product JSON, keyed by id — ids arrive from the DOM as strings. */
    variantById(id) {
        if (!id || !this.product || !Array.isArray(this.product.variants)) {
            return null;
        }

        return this.product.variants.find((v) => String(v.id) === String(id)) || null;
    }

    setStatus(text, state = '') {
        if (!this.status) {
            return;
        }

        window.clearTimeout(this.statusTimer);
        this.status.textContent = text;
        this.status.classList.toggle('is-success', state === 'success');
        this.status.classList.toggle('is-error', state === 'error');

        if (text) {
            this.statusTimer = window.setTimeout(() => {
                this.status.textContent = '';
                this.status.classList.remove('is-success', 'is-error');
            }, STATUS_TIMEOUT);
        }
    }

    resetSelect(trigger) {
        if (!trigger || !trigger.matches(SELECTORS.optionSelect)) {
            return;
        }

        trigger.selectedIndex = 0;
        this.syncDropdown();
    }

    async submit(trigger) {
        // The radio has already flipped by the time we get here — the click
        // handler does not preventDefault — so bailing out silently would leave
        // the shopper looking at a size that was never added. Say so instead.
        if (this.pending) {
            this.setStatus(this.root.dataset.cpcBusyLabel || '');
            return;
        }

        // A pill click lands here while the radio's own `click` event is still
        // propagating, and the spec only fires `change` — and with it the
        // picker's id sync — once that dispatch has finished. Checkedness is
        // already updated though, so resolve the variant from the picker rather
        // than from a hidden input that still holds the previous selection.
        if (this.picker) {
            const variant = this.picker.currentVariant;
            if (!variant) {
                this.setStatus(this.root.dataset.cpcErrorLabel || 'Could not add to bag', 'error');
                return;
            }

            this.picker.update(variant);
        }

        const {id, quantity} = serializeCartForm(this.form);
        if (!id || Number.isNaN(id)) {
            console.error('[CollectionProductCard] could not resolve variant id', this.form);
            return;
        }

        this.pending = true;

        try {
            this.root.classList.add('is-pending');

            if (trigger) {
                trigger.setAttribute('aria-busy', 'true');
            }
            this.setStatus('');

            // Silent: the card renders its own message in the status line
            // below the size box, so an `alert()` on top of it would be a
            // second, more disruptive signal for the same failure.
            const result = await addToCart([{id, quantity}], {silent: true});

            if (result && Array.isArray(result.items) && result.items.length) {
                this.setStatus(this.root.dataset.cpcAddedLabel || 'Added to bag', 'success');
                this.resetSelect(trigger);

                if (typeof window.GAevent === 'function') {
                    window.GAevent('Collection', 'Add To Cart', 'Submit');
                }
            } else {
                const reason = result && (result.description || result.message);
                this.setStatus(reason || this.root.dataset.cpcErrorLabel || 'Could not add to bag', 'error');
            }
        } catch (error) {
            console.error('[CollectionProductCard] add failed', error);
            this.setStatus(this.root.dataset.cpcErrorLabel || 'Could not add to bag', 'error');
        } finally {
            this.pending = false;
            this.root.classList.remove('is-pending');
            if (trigger) {
                trigger.removeAttribute('aria-busy');
            }
        }
    }

    destroy() {
        window.clearTimeout(this.statusTimer);

        if (this.panel) {
            this.stopWatchingWaitlist();

            this.root.removeEventListener('click', this.onWaitlistClick);
            this.root.removeEventListener('keydown', this.onWaitlistKeydown);
            document.removeEventListener(WAITLIST_OPEN_EVENT, this.onOtherWaitlistOpen);
        }

        if (!this.form) {
            return;
        }

        this.form.removeEventListener('click', this.onClick);
        this.form.removeEventListener('keydown', this.onKeydown);
        this.form.removeEventListener('keyup', this.onKeyup);
        this.form.removeEventListener('change', this.onChange);
        this.form.removeEventListener('submit', this.onSubmit);

        if (this.dropdown) {
            this.dropdown.removeEventListener('click', this.onDropdownClick);
            this.dropdown.removeEventListener('keydown', this.onDropdownKeydown);
            document.removeEventListener('pointerdown', this.onDocumentPointerDown);
        }

        if (this.picker) {
            this.picker.destroy();
        }
    }
}
